<template>
    <div class="media-select row equal m-0" :class="containerClass">
        <!-- main //-->
        <div class="col-md-9 flex-column" :class="{'col-md-9': options.sidebar, 'col-md-12': !options.sidebar}">

            <template v-if="options.sets && !options.tree">
                <h4 class="media-gallery-heading mt-0">Collections</h4>
                <gallery
                    :items="filteredSets"
                    item-classes="media-item-container col-md-12 col-lg-6"
                    no-items-msg="No media galleries found."
                    show-no-items-msg
                >
                    <template #item="slotProps">
                        <select-item
                            :item="slotProps.mediaItem"
                            :mode="options.mode"
                            :is-checked="selectedIds.includes(slotProps.mediaItem.id)"
                            v-on:item-clicked="doShiftClick('set', $event)"
                            condensed
                        >
                        </select-item>
                    </template>
                </gallery>
            </template>
            <template v-else-if="options.sets && options.tree">
                <treeselect
                    v-model="value"
                    :flat="true"
                    :multiple="false"
                    :searchable="true"
                    :show-count="true"
                    :options="treeOptions"
                    :load-options="loadTreeOptions"
                    :auto-load-root-options="true"
                    :allow-selecting-disabled-descendants="true"
                    :always-open="true"
                    :normalizer="treeNormalizer"
                    :default-expand-level="2"
                    :max-height="1024"
                    placeholder="Select collection">
                    <label slot="option-label" slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }" :class="labelClassName">
                        <i class="fa" :class="nodeIconClassName(node)"></i>
                        {{ node.label }}
                        <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span>
                    </label>
                </treeselect>
            </template>

            <template v-if="options.media">
                <h4 class="media-gallery-heading">Media Items</h4>
                <gallery
                    :items="filteredMediaItems"
                    item-classes="media-item-container col-xs-12 col-md-6 col-lg-4"
                    fetch-method="fetchMediaSelectMediaItems"
                    :params="fetchOptions"
                    :meta="meta"
                    @items-loaded="$emit('items-loaded', $event)"
                >

                    <template #item="slotProps">
                        <select-item
                            class="col-xs-12"
                            :item="slotProps.mediaItem"
                            :mode="options.mode"
                            :is-checked="selectedIds.includes(slotProps.mediaItem.id)"
                            :selected-getter="selectedGetter"
                            :selected-setter="selectedSetter"
                            v-on:item-clicked="doShiftClick('media', $event)"
                        >
                             <p class="mx-1" v-if="showDimensions && slotProps.mediaItem.hasOriginalDimensions">
                                {{ slotProps.mediaItem.originalDimensions.width }}x{{ slotProps.mediaItem.originalDimensions.height }}
                            </p>
                        </select-item>
                    </template>
                </gallery>
            </template>
        </div>

        <!-- sidebar //-->
        <div v-if="options.sidebar" class="col-md-3 side-bar-container">
            <side-bar :component="sidebar" :context="sidebarContext" />
        </div>
    </div>
</template>

<script>
import { has } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';

import { Treeselect, LOAD_CHILDREN_OPTIONS } from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

import Gallery from '@/components/media/Gallery';
import SelectItem from '@/components/media/select/SelectItem';
import SideBar from '@/components/media/select/side-bar';

// mixins
import validators from '@/components/media/select/mixins/validators';
import selection from '@/components/media/select/mixins/selection';
import sidebarContext from '@/components/media/select/mixins/sidebarContext';

// helpers
import { getSelection } from '@/helper/select';

export default {
    name: 'MediaSelect',
    mixins: [
        validators,
        selection,
        sidebarContext,
    ],
    components: {
        Gallery,
        SelectItem,
        SideBar,
        Treeselect,
    },
    props: {
        items: {
            type: Object,
            required: true,
        },
        fetchOptions: {
            type: Object,
            required: true,
        },
        meta: {
            type: Object,
            required: true,
        },
        selectedGetter: {
            type: String,
            default: 'getMediaSelectSelected',
        },
        selectedSetter: {
            type: String,
            default: 'addMediaSelectItem',
        },
        clearSelected: {
            type: Boolean,
        },
        showDimensions: {
            type: Boolean,
            default: false,
        },
    },
    errorCaptured() {
        return false;
    },
    data() {
        return {
            value: null,
            treeOptions: [{
                id: 0,
                title: 'Media Gallery (root)',
                hasChildren: true,
                children: null,
            }],
        }
    },
    async mounted() {
        const that = this;
        this.setValidator(this.$v); // add validator to store
        this.$v.$touch(); // init all validation
    },
    computed: {
        ...mapGetters({
            options: 'getMediaSelectOptions',
            getItem: 'getMediaSelectItemById',
            sidebar: 'getMediaSelectSidebar',
            strategyManager: 'getMediaSelectStrategyManager',
            getMediaSetItems: 'getMediaSetItems',
            getMediaSetSelectedIds: 'getMediaSetSelectedIds',
        }),
        selected() {
            return this.$store.getters[this.selectedGetter];
        },
        selectedIds() {
            let ids = [];
            if (Array.isArray(this.selected) && this.selected.length > 0) {
                ids = this.selected.map(i => i.id);
            }
            return ids;
        },
        selectedCount() {
            let count = 0;
            if (Array.isArray(this.selected) && this.selected.length > 0) {
                count = this.selected.length;
            }
            return count;
        },
        strategy() {
            return this.strategyManager.strategy;
        },
        filteredMediaItems() { // TODO: revise how items are set in modal component maybe strategies should handle this or the store action?
            let items = [];
            if (Object.keys(this.items).length > 0) {
                items = this.items.media.data;
                const setStrategies = ['AddItemsToSet', 'AddItemsToExistingSet'];
                const strategies = ['ChangeThumbnail', 'ChangeSetThumbnail', 'UploadNewVersion'];
                const mediaSetItems = this.getMediaSetItems.map(i => i.id);

                // need to filter out own self
                if (this.options.strategy && strategies.includes(this.options.strategy)) {
                    items = items.filter(item => item.id !== this.options.itemId);
                }
                // For sets do not allow media items alreday in the set
                if (this.options.strategy && setStrategies.includes(this.options.strategy)) {
                    items = items.filter((item) => {
                        return mediaSetItems && !mediaSetItems.includes(item.id);
                    })
                }
            }
            return items;
        },
        filteredSets() {
            let items = [];
            if (Object.keys(this.items).length > 0) {
                if (has(this.items, 'sets')) {
                    const itemId = this.options.itemId;
                    const sets = this.items.sets.data;
                    if (itemId) {
                        const item = sets.find(i => i.id === itemId);
                        // do not include own self or existing children
                        if (item) {
                            items = sets.filter(i => i.id !== item.id);
                            items = items.filter(i => !item.childIds.includes(i.id));
                        }
                    } else {
                        // dont include self if in selection
                        items = sets.filter(i => !this.selectedIds.includes(i.id));
                    }
                }
            }
            return items;
        },
        containerClass() {
            return this.strategyManager ? this.strategyManager.getCSSClassName() : '';
        }
    },
    watch: {
        selectedCount() {
            this.$v.selectedCount.$touch(); // trigger selectedCount validations
        },
        value(newValue) {
            if (newValue) {
                this.addMediaSelectItem({
                    id: newValue,
                    checked: !this.isChecked,
                    mode: 'single',
                    type: 'set',
                });
            }
        },
        clearSelected(newValue) {
            if (newValue) {
                this.value = null;
                this.$emit('selected-cleared');
            }
        },
    },
    methods: {
        ...mapMutations({
            setValidator: 'setMediaSelectValidator',
        }),
        ...mapActions(['fetchMediaSelectMediaItems', 'fetchMediaSets', 'addMediaSelectItem', 'addMediaSelectItem']),
        async loadTreeOptions({ action, parentNode, callback }) {
            if (action === LOAD_CHILDREN_OPTIONS) {
                const parentId = parentNode.id !== 0 ? parentNode.id : null;
                await this.fetchMediaSets({
                    include: 'children',
                    filter: { parent_id: parentId, type: 'gallery', project_id: this.project.uuid },
                    limit: -1,
                }).then((response) => {
                    parentNode.children = {data: response.data};
                });
            }
        },
        treeNormalizer(node) {
            const data = {
                id: node.id,
                label: node.title,
            }

            if (!has(node, 'children') || has(node, 'children') && has(node.children, 'data') && node.children.data.length === 0) {
                delete node.children;
            } else {
                data.children = node.children ? node.children.data : null;
            }

            if (node.id === this.options.itemId) {

                if (has(data, 'children')) {
                    data.children.forEach((child, index) => {

                        const selected = App.$store.getters.getMediaSetSelectedIds;

                        if (selected.includes(child.id)) {
                            child.isDisabled = true;
                        } else {
                            child.isDisabled = false;
                        }
                    });
                }

                data.isDisabled = true;
            }

            return data;
        },
        nodeIconClassName(node) {
            let className = '';
            if (!node.id) {
                className = 'fa-images';
            } else if (node.isBranch) {
                className = 'fa-folder';
            } else {
                className = 'fa-th-large';
            }
            return className;
        },
        doShiftClick(type, {e, item}) {
            try {
                if (e.shiftKey && this.options.mode === 'multiple') {
                    let items = [];

                    // get items based on gallery type
                    if (type === 'media') {
                        items = [...this.filteredMediaItems];
                    } else if (type === 'set') {
                        items = [...this.filteredSets];
                    }

                    // filter selected by gallery type
                    const selected = type === 'media' ?
                        this.selected.filter(i => i.type !== 'gallery') :
                        type === 'set' ?
                            this.selected.filter(i => i.type === 'gallery') :
                            [];

                    // if more than one item is selected
                    if (this.selected.length > 1) {

                        const newSelected = getSelection(items, this.selected);

                        // set items to be selected skipping ones already in selection
                        newSelected.forEach((item) => {
                            if (!this.selected.map(i => i.id).includes(item.id)) {
                                this.$store.dispatch(this.selectedSetter, {
                                    id: item.id,
                                    checked: true,
                                    type: item.type,
                                    mode: this.options.mode,
                                });
                            }
                        });
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },
    },
}
</script>
